import { useState, useEffect, useRef, useCallback } from 'react';
import api from '@utils/api';
import { useI18n } from '@/i18n';
import PageHeader from '@components/PageHeader';
import { Toast } from '@components/AbsenceBits';
import { IconChevron } from '@components/icons';
import { lisbonToday, fmtDate, fmtDateTime, fmtDays, kindLabel, apiMessage } from '@utils/absences';

const STATUS_CLS = {
  pending:   'bg-amber-100 text-amber-900',
  approved:  'bg-green-100 text-green-800',
  rejected:  'bg-red-100 text-red-800',
  cancelled: 'bg-gray-200 text-gray-700',
};
const FIELD = 'input min-h-[44px] !text-base md:!text-sm';

// Last hr_question not yet followed by an answer (and the request still open to talk about).
function openQuestion(r) {
  if (r.status === 'cancelled') return null;
  const evs = r.events || [];
  for (let i = evs.length - 1; i >= 0; i -= 1) {
    if (evs[i].event === 'answer') return null;
    if (evs[i].event === 'hr_question') return evs[i];
  }
  return null;
}

export default function Absences() {
  const { t } = useI18n();
  const [kinds, setKinds] = useState([]);
  const [balance, setBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [toast, setToast] = useState(null);
  const loadSeq = useRef(0);

  const load = useCallback(() => {
    const seq = ++loadSeq.current;
    return Promise.all([
      api.get('/absences/kinds'),
      api.get('/absences/me/balance'),
      api.get('/absences/me'),
    ]).then(([k, b, m]) => {
      if (seq !== loadSeq.current) return;
      setKinds(k.data.kinds || []);
      setBalance(b.data);
      setRequests(m.data.requests || []);
      setLoadError('');
    }).catch((err) => {
      if (seq !== loadSeq.current) return;
      // 404 on balance = no staff profile linked to this account.
      setLoadError(err?.response?.status === 404 ? t('abs.noProfile') : t('abs.loadError'));
    }).finally(() => { if (seq === loadSeq.current) setLoading(false); });
  }, [t]);
  useEffect(() => { load(); }, [load]);

  // Refresh just the balance + history after a change (the form keeps its state).
  const refresh = useCallback(() => load(), [load]);
  const flash = (kind, text) => { setToast({ kind, text }); };

  return (
    <>
      <PageHeader title={t('abs.title')} subtitle={t('abs.subtitle')} />
      <div className="px-5 md:px-0 pb-6 space-y-4">
        <Toast toast={toast} />
        {loading ? (
          <div className="card p-8 text-center text-gray-500">{t('common.loading')}</div>
        ) : loadError ? (
          <div role="alert" className="card p-6 text-sm text-red-700">{loadError}</div>
        ) : (
          <>
            <BalanceCard balance={balance} />
            <RequestForm kinds={kinds} onDone={(msg) => { flash(msg.kind, msg.text); refresh(); }} />
            <History requests={requests} onChanged={refresh} flash={flash} />
          </>
        )}
      </div>
    </>
  );
}

function BalanceCard({ balance }) {
  const { t, lang } = useI18n();
  if (!balance) return null;
  return (
    <section className="card p-5" aria-label={t('abs.balance')}>
      <h2 className="kv-label">{t('abs.balanceYear', { year: balance.year })}</h2>
      <div className="mt-1 flex items-baseline gap-2">
        <span data-testid="balance-available" className="text-3xl font-semibold text-glk-dark">{fmtDays(balance.available, lang)}</span>
        <span className="text-sm text-gray-600">{t('abs.availableOf', { total: fmtDays(balance.total + balance.usable_carry, lang) })}</span>
      </div>
      <dl className="mt-3 grid grid-cols-2 gap-x-4 gap-y-1 text-sm">
        <div><dt className="text-gray-600">{t('abs.entitlement')}</dt><dd className="font-medium">{fmtDays(balance.total, lang)}</dd></div>
        <div><dt className="text-gray-600">{t('abs.used')}</dt><dd className="font-medium">{fmtDays(balance.used, lang)}</dd></div>
        <div><dt className="text-gray-600">{t('abs.pendingDays')}</dt><dd className="font-medium">{fmtDays(balance.pending, lang)}</dd></div>
      </dl>
      {balance.usable_carry > 0 && (
        <p className="mt-3 text-sm text-gray-600" data-testid="balance-carry">
          {t('abs.carry', { days: fmtDays(balance.usable_carry, lang) })}
          {balance.carry_expires_on ? ` ${t('abs.carryExpires', { date: fmtDate(balance.carry_expires_on, lang) })}` : ''}
        </p>
      )}
    </section>
  );
}

function RequestForm({ kinds, onDone }) {
  const { t, lang } = useI18n();
  const requestable = kinds.filter((k) => k.requestable);
  const [kind, setKind] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [halfStart, setHalfStart] = useState(false);
  const [halfEnd, setHalfEnd] = useState(false);
  const [note, setNote] = useState('');
  const [file, setFile] = useState(null);
  const [fileKey, setFileKey] = useState(0);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // idle | loading | ok | error  (+ data from the API)
  const [preview, setPreview] = useState({ state: 'idle' });
  const seq = useRef(0);

  // Vacation is what most people file; the API lists kinds alphabetically, which put maternity leave first.
  const selected = kinds.find((k) => k.code === (kind || (requestable.find((k2) => k2.code === 'vacation') || requestable[0])?.code));
  const kindCode = selected?.code || '';
  const sensitive = !!selected?.sensitive;
  const rangeOk = !!kindCode && !!start && !!end && end >= start;

  useEffect(() => {
    const mine = ++seq.current;
    if (!rangeOk) { setPreview({ state: 'idle' }); return undefined; }
    setPreview({ state: 'loading' });
    const timer = setTimeout(() => {
      api.post('/absences/preview', {
        kind: kindCode, start_date: start, end_date: end, half_day_start: halfStart, half_day_end: halfEnd,
      }).then(({ data }) => {
        if (mine !== seq.current) return;               // an older response must not overwrite a newer one
        setPreview({ state: data.error ? 'error' : 'ok', data });
      }).catch((err) => {
        if (mine !== seq.current) return;
        setPreview({ state: 'error', data: { error: apiMessage(err, t('abs.previewFailed')) } });
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [rangeOk, kindCode, start, end, halfStart, halfEnd, t]);

  const canSubmit = preview.state === 'ok' && !busy && rangeOk;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    setBusy(true); setError('');
    let created;
    try {
      const body = { kind: kindCode, start_date: start, end_date: end, half_day_start: halfStart, half_day_end: halfEnd };
      if (!sensitive && note.trim()) body.note = note.trim();
      created = (await api.post('/absences', body)).data;
    } catch (err) {
      setError(apiMessage(err, t('abs.submitFailed')));
      setBusy(false);
      return;
    }
    let certFailed = false;
    if (sensitive && file) {
      try {
        const form = new FormData();
        form.append('file', file);
        await api.post(`/absences/${created.id}/certificate`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      } catch { certFailed = true; }
    }
    setBusy(false);
    setStart(''); setEnd(''); setHalfStart(false); setHalfEnd(false); setNote(''); setFile(null); setFileKey((k) => k + 1);
    onDone(certFailed ? { kind: 'error', text: t('abs.createdCertFailed') } : { kind: 'ok', text: t('abs.created') });
  };

  const pv = preview.data;
  const years = pv?.by_year ? Object.entries(pv.by_year) : [];

  return (
    <form onSubmit={submit} className="card p-5 space-y-3" aria-label={t('abs.newRequest')}>
      <h2 className="text-sm font-semibold text-glk-dark">{t('abs.newRequest')}</h2>
      <div>
        <label className="label" htmlFor="abs-kind">{t('abs.kind')}</label>
        <select id="abs-kind" className={FIELD} value={kindCode} onChange={(e) => { setKind(e.target.value); setNote(''); setFile(null); setFileKey((k) => k + 1); }}>
          {requestable.map((k) => <option key={k.code} value={k.code}>{lang === 'en' ? k.label_en : k.label_pt}</option>)}
        </select>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="min-w-0">
          <label className="label" htmlFor="abs-start">{t('abs.from')}</label>
          <input id="abs-start" type="date" className={FIELD} value={start} onChange={(e) => { setStart(e.target.value); if (end && e.target.value > end) setEnd(e.target.value); }} />
        </div>
        <div className="min-w-0">
          <label className="label" htmlFor="abs-end">{t('abs.to')}</label>
          <input id="abs-end" type="date" className={FIELD} value={end} min={start || undefined} onChange={(e) => setEnd(e.target.value)} />
        </div>
      </div>
      <div className="flex flex-wrap gap-x-5 gap-y-1">
        <label className="inline-flex items-center gap-2 min-h-[44px] text-sm cursor-pointer">
          <input type="checkbox" className="h-5 w-5 accent-glk-green" checked={halfStart} onChange={(e) => setHalfStart(e.target.checked)} />
          {t('abs.halfStart')}
        </label>
        <label className="inline-flex items-center gap-2 min-h-[44px] text-sm cursor-pointer">
          <input type="checkbox" className="h-5 w-5 accent-glk-green" checked={halfEnd} onChange={(e) => setHalfEnd(e.target.checked)} />
          {t('abs.halfEnd')}
        </label>
      </div>

      {rangeOk ? (
        <div className="rounded-lg bg-glk-light border border-gray-200 px-3 py-2.5 text-sm" aria-live="polite" data-testid="preview">
          {preview.state === 'loading' && <span className="text-gray-600">{t('abs.calculating')}</span>}
          {preview.state === 'error' && <span role="alert" className="text-red-700">{pv?.error}</span>}
          {preview.state === 'ok' && (
            <div className="space-y-0.5">
              <p className="font-semibold text-glk-dark" data-testid="preview-days">{t('abs.workingDays', { n: fmtDays(pv.days, lang) })}</p>
              {years.length > 1 && (
                <p className="text-gray-700" data-testid="preview-years">
                  {years.map(([y, n]) => `${y}: ${fmtDays(n, lang)}`).join(' · ')}
                </p>
              )}
              {pv.available_after !== null && pv.available_after !== undefined && (
                <p className="text-gray-700">{t('abs.balanceAfter', { n: fmtDays(pv.available_after, lang) })}</p>
              )}
            </div>
          )}
        </div>
      ) : (start && end && end < start) ? (
        <p role="alert" className="text-sm text-red-700">{t('abs.endBeforeStart')}</p>
      ) : null}

      {sensitive ? (
        <div className="rounded-lg border border-gray-200 p-3 space-y-2">
          <label className="label !mb-0" htmlFor="abs-cert">{t('abs.certificate')}</label>
          <input key={fileKey} id="abs-cert" type="file" accept="application/pdf,image/jpeg,image/png"
                 className="block w-full text-base md:text-sm min-h-[44px] file:mr-3 file:min-h-[44px] file:px-3 file:rounded-lg file:border file:border-gray-300 file:bg-white"
                 onChange={(e) => setFile(e.target.files?.[0] || null)} />
          <p className="text-xs text-gray-600" data-testid="cert-privacy">{t('abs.certificatePrivacy')}</p>
        </div>
      ) : (
        <div>
          <label className="label" htmlFor="abs-note">{t('abs.noteOptional')}</label>
          <textarea id="abs-note" className={`${FIELD} resize-none`} rows={2} maxLength={1000} value={note} onChange={(e) => setNote(e.target.value)} />
        </div>
      )}

      {error && <div role="alert" className="px-3 py-2 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}
      <button type="submit" disabled={!canSubmit} className="btn-primary w-full min-h-[44px]">
        {busy ? t('abs.sending') : t('abs.submit')}
      </button>
    </form>
  );
}

function History({ requests, onChanged, flash }) {
  const { t } = useI18n();
  return (
    <section aria-label={t('abs.history')}>
      <h2 className="text-sm font-semibold text-glk-dark mb-2">{t('abs.history')}</h2>
      {requests.length === 0 ? (
        <div className="card p-6 text-center text-gray-600 text-sm">{t('abs.empty')}</div>
      ) : (
        <ul className="space-y-2">
          {requests.map((r) => <RequestItem key={r.id} r={r} onChanged={onChanged} flash={flash} />)}
        </ul>
      )}
    </section>
  );
}

function RequestItem({ r, onChanged, flash }) {
  const { t, lang } = useI18n();
  const [open, setOpen] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState('');
  const today = lisbonToday();
  const canCancel = r.status === 'pending' || (r.status === 'approved' && r.start_date > today);
  const question = openQuestion(r);
  const trail = (r.events || []).filter((e) => e.event !== 'reminder_sent');

  const post = async (body, okText) => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/absences/${r.id}/events`, body);
      setConfirming(false); setAnswer('');
      flash('ok', okText);
      onChanged();
    } catch (err) {
      flash('error', apiMessage(err, t('abs.actionFailed')));
      onChanged();
    } finally { setBusy(false); }
  };

  const range = r.start_date === r.end_date
    ? fmtDate(r.start_date, lang)
    : `${fmtDate(r.start_date, lang)} – ${fmtDate(r.end_date, lang)}`;

  return (
    <li className="card">
      <button type="button" onClick={() => setOpen((o) => !o)} aria-expanded={open}
              className="w-full text-left p-4 flex items-center gap-3 min-h-[44px]">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-sm font-medium text-glk-dark">{kindLabel(r, lang)}</span>
            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_CLS[r.status] || STATUS_CLS.pending}`}>{t(`abs.status.${r.status}`)}</span>
            {question && <span className="inline-flex px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-900">{t('abs.hrAsked')}</span>}
          </div>
          <p className="text-xs text-gray-600 mt-0.5">{range} · {t('abs.daysShort', { n: fmtDays(r.days, lang) })}</p>
        </div>
        <IconChevron size={18} className={`text-gray-400 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`} />
      </button>

      {(open || question) && (
        <div className="px-4 pb-4 space-y-3 border-t border-gray-100 pt-3">
          {r.note && <p className="text-sm text-gray-700 whitespace-pre-wrap">{r.note}</p>}
          <ol className="space-y-2" aria-label={t('abs.trail')}>
            {trail.map((e) => (
              <li key={e.id} className="text-sm">
                <span className="font-medium text-glk-dark">{t(`abs.event.${e.event}`)}</span>
                <span className="text-gray-600"> · {e.actor_name || t('abs.unknownActor')} · {fmtDateTime(e.created_at, lang)}</span>
                {e.decided_as && <span className="ml-1.5 inline-flex px-1.5 py-0.5 rounded text-[11px] font-medium bg-gray-100 text-gray-700">{t(`abs.decidedAs.${e.decided_as}`)}</span>}
                {e.comment && <p className="mt-0.5 text-gray-700 whitespace-pre-wrap">{e.comment}</p>}
              </li>
            ))}
          </ol>

          {question && (
            <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 space-y-2">
              <p className="text-sm font-medium text-blue-900">{t('abs.hrAskedFull')}</p>
              <p className="text-sm text-blue-900 whitespace-pre-wrap">{question.comment}</p>
              <label className="label" htmlFor={`ans-${r.id}`}>{t('abs.yourAnswer')}</label>
              <textarea id={`ans-${r.id}`} className={`${FIELD} resize-none`} rows={2} maxLength={2000} value={answer} onChange={(e) => setAnswer(e.target.value)} />
              <button type="button" className="btn-primary min-h-[44px]" disabled={busy || !answer.trim()}
                      onClick={() => post({ event: 'answer', comment: answer.trim() }, t('abs.answerSent'))}>
                {t('abs.sendAnswer')}
              </button>
            </div>
          )}

          {canCancel && !confirming && (
            <button type="button" className="btn-ghost min-h-[44px]" onClick={() => setConfirming(true)}>{t('abs.cancel')}</button>
          )}
          {canCancel && confirming && (
            <div role="alertdialog" aria-label={t('abs.cancelConfirm')} className="rounded-lg border border-red-200 bg-red-50 p-3 space-y-2">
              <p className="text-sm text-red-900">{t('abs.cancelConfirm')}</p>
              <div className="flex gap-2">
                <button type="button" className="btn-ghost flex-1 min-h-[44px]" onClick={() => setConfirming(false)} disabled={busy}>{t('abs.keep')}</button>
                {/* No comment is ever sent on cancel: for sensitive kinds it would be readable by approvers. */}
                <button type="button" className="btn-primary flex-1 min-h-[44px] !bg-red-700 hover:!bg-red-800" disabled={busy}
                        onClick={() => post({ event: 'cancelled' }, t('abs.cancelled'))}>{t('abs.confirmCancel')}</button>
              </div>
            </div>
          )}
        </div>
      )}
    </li>
  );
}
